import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  Split,
  Users,
  DollarSign,
  Loader2,
  CreditCard,
  Banknote,
  QrCode,
  Smartphone,
} from 'lucide-react';

export const SplitBillModal = ({ open, onOpenChange, order, onSuccess }) => {
  const { api } = useAuth();
  const { formatCurrency } = useCurrency();
  const [splitMode, setSplitMode] = useState('items'); // 'items' or 'amount'
  const [splits, setSplits] = useState([{ items: [], amount: 0 }, { items: [], amount: 0 }]);
  const [loading, setLoading] = useState(false);
  const [payingSplitId, setPayingSplitId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [existingSplits, setExistingSplits] = useState([]);

  useEffect(() => {
    if (open && order) {
      fetchExistingSplits();
      // Initialize with 2 splits
      if (!order.is_split) {
        setSplits([{ items: [], amount: 0 }, { items: [], amount: 0 }]);
      }
    }
  }, [open, order]);

  const fetchExistingSplits = async () => {
    if (!order?.id) return;
    try {
      const resp = await api.get(`/orders/${order.id}/splits`);
      setExistingSplits(resp.data);
    } catch (error) {
      console.error('Failed to fetch splits:', error);
    }
  };

  const addSplit = () => {
    setSplits([...splits, { items: [], amount: 0 }]);
  };

  const removeSplit = (index) => {
    if (splits.length <= 2) return;
    setSplits(splits.filter((_, i) => i !== index));
  };

  const toggleItemInSplit = (splitIndex, itemId, quantity) => {
    setSplits(prev => prev.map((split, idx) => {
      if (idx === splitIndex) {
        const existingIdx = split.items.findIndex(i => i.order_item_id === itemId);
        if (existingIdx >= 0) {
          // Remove item
          return { ...split, items: split.items.filter(i => i.order_item_id !== itemId) };
        } else {
          // Add item
          return { ...split, items: [...split.items, { order_item_id: itemId, quantity }] };
        }
      }
      return split;
    }));
  };

  const updateSplitAmount = (index, amount) => {
    setSplits(prev => prev.map((split, idx) => 
      idx === index ? { ...split, amount: parseFloat(amount) || 0 } : split
    ));
  };

  const calculateSplitTotal = (split) => {
    if (splitMode === 'amount') {
      return split.amount * 1.1; // Add 10% tax
    }
    
    let subtotal = 0;
    for (const splitItem of split.items) {
      const orderItem = order.items.find(i => i.id === splitItem.order_item_id);
      if (orderItem) {
        const itemPrice = (orderItem.unit_price * splitItem.quantity) + 
          (orderItem.add_on_total * splitItem.quantity / orderItem.quantity);
        subtotal += itemPrice;
      }
    }
    return subtotal * 1.1; // Add 10% tax
  };

  const handleCreateSplits = async () => {
    setLoading(true);
    try {
      const splitData = splits.map(split => ({
        items: splitMode === 'items' ? split.items : [],
        amount: splitMode === 'amount' ? split.amount : 0,
      }));

      await api.post(`/orders/${order.id}/split`, splitData);
      toast.success('Order split successfully!');
      fetchExistingSplits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to split order');
    } finally {
      setLoading(false);
    }
  };

  const handlePaySplit = async (splitId) => {
    const split = existingSplits.find(s => s.id === splitId);
    if (!split) return;

    setLoading(true);
    try {
      await api.post(`/splits/${splitId}/pay`, {
        order_id: order.id,
        outlet_id: order.outlet_id,
        method: paymentMethod,
        amount: split.total,
      });
      toast.success('Split bill paid!');
      fetchExistingSplits();
      setPayingSplitId(null);
      
      // Check if all paid
      const updatedSplits = await api.get(`/orders/${order.id}/splits`);
      if (updatedSplits.data.every(s => s.is_paid)) {
        toast.success('All splits paid! Order completed.');
        onSuccess?.();
        onOpenChange(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="h-5 w-5" />
            Split Bill - #{order.order_number}
          </DialogTitle>
        </DialogHeader>

        {existingSplits.length > 0 ? (
          /* Show existing splits */
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This order has been split into {existingSplits.length} bills.
            </p>
            
            <div className="grid gap-4">
              {existingSplits.map((split, idx) => (
                <div
                  key={split.id}
                  className={`p-4 border rounded-lg ${split.is_paid ? 'bg-green-500/10 border-green-500' : ''}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">Bill #{idx + 1}</h3>
                    {split.is_paid ? (
                      <Badge className="bg-green-500">Paid</Badge>
                    ) : (
                      <Badge variant="outline">Unpaid</Badge>
                    )}
                  </div>
                  
                  <div className="space-y-1 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-mono">{formatCurrency(split.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax (10%)</span>
                      <span className="font-mono">{formatCurrency(split.tax)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-base pt-2 border-t">
                      <span>Total</span>
                      <span className="font-mono">{formatCurrency(split.total)}</span>
                    </div>
                  </div>

                  {!split.is_paid && (
                    payingSplitId === split.id ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-4 gap-2">
                          <Button
                            size="sm"
                            variant={paymentMethod === 'CASH' ? 'default' : 'outline'}
                            onClick={() => setPaymentMethod('CASH')}
                          >
                            <Banknote className="h-3 w-3 mr-1" />
                            Cash
                          </Button>
                          <Button
                            size="sm"
                            variant={paymentMethod === 'QRIS' ? 'default' : 'outline'}
                            onClick={() => setPaymentMethod('QRIS')}
                          >
                            <QrCode className="h-3 w-3 mr-1" />
                            QRIS
                          </Button>
                          <Button
                            size="sm"
                            variant={paymentMethod === 'BANK_TRANSFER' ? 'default' : 'outline'}
                            onClick={() => setPaymentMethod('BANK_TRANSFER')}
                          >
                            <CreditCard className="h-3 w-3 mr-1" />
                            Transfer
                          </Button>
                          <Button
                            size="sm"
                            variant={paymentMethod === 'E_WALLET' ? 'default' : 'outline'}
                            onClick={() => setPaymentMethod('E_WALLET')}
                          >
                            <Smartphone className="h-3 w-3 mr-1" />
                            E-Wallet
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPayingSplitId(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handlePaySplit(split.id)}
                            disabled={loading}
                          >
                            {loading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                            Confirm Payment
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => setPayingSplitId(split.id)}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Pay This Bill
                      </Button>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Create new splits */
          <Tabs value={splitMode} onValueChange={setSplitMode}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="items">
                <Users className="h-4 w-4 mr-2" />
                Split by Items
              </TabsTrigger>
              <TabsTrigger value="amount">
                <DollarSign className="h-4 w-4 mr-2" />
                Split by Amount
              </TabsTrigger>
            </TabsList>

            <TabsContent value="items" className="mt-4">
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Assign items to different bills. Each item can only be in one bill.
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  {splits.map((split, splitIdx) => (
                    <div key={splitIdx} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Bill #{splitIdx + 1}</h4>
                        {splits.length > 2 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSplit(splitIdx)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      
                      <ScrollArea className="h-48 pr-2">
                        {order.items.map((item) => {
                          const isSelected = split.items.some(i => i.order_item_id === item.id);
                          const isInOtherSplit = splits.some((s, idx) => 
                            idx !== splitIdx && s.items.some(i => i.order_item_id === item.id)
                          );
                          
                          return (
                            <div
                              key={item.id}
                              className={`flex items-center gap-2 p-2 rounded ${isInOtherSplit ? 'opacity-50' : ''}`}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={isInOtherSplit}
                                onCheckedChange={() => toggleItemInSplit(splitIdx, item.id, item.quantity)}
                              />
                              <div className="flex-1">
                                <p className="text-sm">{item.quantity}x {item.menu_item_name}</p>
                              </div>
                            </div>
                          );
                        })}
                      </ScrollArea>
                      
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex justify-between font-medium">
                          <span>Total</span>
                          <span className="font-mono">{formatCurrency(calculateSplitTotal(split))}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button variant="outline" onClick={addSplit}>
                  <Users className="h-4 w-4 mr-2" />
                  Add Another Bill
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="amount" className="mt-4">
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Split the bill by amount. Tax will be calculated separately for each.
                </div>
                
                <div className="p-4 bg-muted rounded-lg mb-4">
                  <div className="flex justify-between font-medium">
                    <span>Order Total (before tax)</span>
                    <span className="font-mono">{formatCurrency(order.subtotal)}</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {splits.map((split, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <Label className="w-20">Bill #{idx + 1}</Label>
                      <Input
                        type="number"
                        value={split.amount || ''}
                        onChange={(e) => updateSplitAmount(idx, e.target.value)}
                        placeholder="Enter amount"
                        className="flex-1"
                      />
                      <span className="text-sm text-muted-foreground w-32">
                        +Tax: {formatCurrency(calculateSplitTotal(split))}
                      </span>
                      {splits.length > 2 && (
                        <Button variant="ghost" size="sm" onClick={() => removeSplit(idx)}>
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <Button variant="outline" onClick={addSplit}>
                  <Users className="h-4 w-4 mr-2" />
                  Add Another Bill
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {existingSplits.length === 0 && (
            <Button onClick={handleCreateSplits} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Split Bills
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
